import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import {
  DAILY_LIFE_COPY,
  EMPTY_STATES,
  EVENT_CATEGORY_META,
  type EventCategory,
} from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { api } from '@/lib/api';
import { cn, todayISO } from '@/lib/format';
import type { CalendarEvent } from '@/types';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** 일정표 — 명세서 §5.6 */
export function SchedulePage() {
  const today = todayISO();
  const [cursor, setCursor] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7)),
  }));
  const [selected, setSelected] = useState<string | null>(today);

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['events', cursor.year, cursor.month],
    queryFn: () => api<CalendarEvent[]>(`/events?year=${cursor.year}&month=${cursor.month}`),
    placeholderData: keepPreviousData, // 달 이동 시 달력 유지
  });

  const events = useMemo(() => data?.data ?? [], [data]);

  /** 날짜별 일정 — 여러 날에 걸친 일정은 시작일에만 표시한다 */
  const byDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const event of events) {
      const key = seoulDateOf(event.startAt);
      map.set(key, [...(map.get(key) ?? []), event]);
    }
    return map;
  }, [events]);

  const cells = useMemo(() => buildMonthCells(cursor.year, cursor.month), [cursor]);
  const selectedEvents = selected ? (byDate.get(selected) ?? []) : [];

  const shift = (delta: number) => {
    setSelected(null);
    setCursor((prev) => {
      const next = prev.month + delta;
      if (next < 1) return { year: prev.year - 1, month: 12 };
      if (next > 12) return { year: prev.year + 1, month: 1 };
      return { year: prev.year, month: next };
    });
  };

  return (
    <Screen>
      <AppBar title="일정표" back />

      <div className="flex items-center justify-between px-4 py-3">
        <button type="button" onClick={() => shift(-1)} aria-label="이전 달" className="tap p-2">
          <ChevronLeft size={20} />
        </button>
        <p className="text-lg font-bold">
          {cursor.year}년 {cursor.month}월
        </p>
        <button type="button" onClick={() => shift(1)} aria-label="다음 달" className="tap p-2">
          <ChevronRight size={20} />
        </button>
      </div>

      {isError && <ErrorNote message="일정을 불러오지 못했어요." onRetry={() => void refetch()} />}

      <div className="px-2">
        <div className="grid grid-cols-7 pb-1">
          {WEEKDAYS.map((day) => (
            <div key={day} className="py-1 text-center text-sm text-ink-sub">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((cell) => {
            const dayEvents = cell.date ? (byDate.get(cell.date) ?? []) : [];
            return (
              <button
                key={cell.key}
                type="button"
                disabled={!cell.date}
                onClick={() => setSelected(cell.date)}
                className={cn(
                  'flex aspect-square flex-col items-center justify-start rounded-sm p-1 text-sm',
                  cell.date === today && 'bg-primary-soft font-bold text-primary-strong',
                  cell.date === selected && cell.date !== today && 'bg-bg',
                  cell.date === selected && 'ring-2 ring-primary',
                )}
              >
                <span className={cn(!cell.date && 'opacity-0')}>{cell.day}</span>
                <span className="mt-0.5 flex flex-wrap justify-center gap-0.5">
                  {dayEvents.slice(0, 3).map((event) => (
                    <span
                      key={event.id}
                      className={cn('h-1.5 w-1.5 rounded-full', dotClass(event.category))}
                    />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {isPending && <Spinner />}

      <div className="flex flex-col gap-3 p-4">
        {selected && selectedEvents.length === 0 && !isPending && (
          <EmptyState title={EMPTY_STATES.schedule.title} body={EMPTY_STATES.schedule.body} />
        )}

        {selectedEvents.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </Screen>
  );
}

export function EventCard({ event }: { event: CalendarEvent }) {
  const meta = EVENT_CATEGORY_META[event.category as EventCategory] ?? EVENT_CATEGORY_META.ETC;

  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <Chip tone={chipTone(event.category)}>{meta.label}</Chip>
        {event.classRoom && <Chip tone="neutral">{event.classRoom.name}</Chip>}
      </div>
      <p className="font-semibold">{event.title}</p>
      {event.description && <p className="mt-1 text-base text-ink-sub">{event.description}</p>}
      {event.supplies && (
        <p className="mt-2 flex items-center gap-1.5 rounded-sm bg-secondary-soft px-3 py-2 text-sm">
          <Package size={14} />
          {DAILY_LIFE_COPY.schedule.suppliesLabel}: {event.supplies}
        </p>
      )}
    </Card>
  );
}

/** 일정은 발달·기록 화면이 아니므로 피치·라벤더를 써도 된다 (§7.1) */
function chipTone(category: string): 'primary' | 'secondary' | 'info' | 'neutral' {
  const tone = EVENT_CATEGORY_META[category as EventCategory]?.tone;
  if (tone === 'sprout') return 'primary';
  if (tone === 'peach') return 'secondary';
  if (tone === 'info') return 'info';
  return 'neutral';
}

function dotClass(category: string): string {
  const tone = EVENT_CATEGORY_META[category as EventCategory]?.tone;
  if (tone === 'sprout') return 'bg-primary';
  if (tone === 'peach') return 'bg-secondary';
  if (tone === 'info') return 'bg-info';
  return 'bg-line-strong';
}

/** ISO 문자열을 서울 기준 `YYYY-MM-DD` 로 — 자정 근처 일정이 하루 밀리지 않게 (§4) */
function seoulDateOf(value: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date(value));
}

interface Cell {
  key: string;
  day: number | null;
  date: string | null;
}

function buildMonthCells(year: number, month: number): Cell[] {
  // UTC 기준으로 계산하고 문자열로만 다룬다 — 로컬 타임존이 끼어들 여지를 없앤다.
  const first = new Date(Date.UTC(year, month - 1, 1));
  const leading = first.getUTCDay();
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();

  const cells: Cell[] = [];
  for (let i = 0; i < leading; i += 1) cells.push({ key: `pad-${i}`, day: null, date: null });
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push({ key: date, day, date });
  }
  return cells;
}
